import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, AlertTriangle, ExternalLink, Search, Building2, MapPin, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

/**
 * Dialog that runs the AI verification on a contact (Tavily + Claude) and lets the
 * user accept the suggested entity (creating a new company on the fly) or detach.
 */
export default function VerifyEntityDialog({ open, onOpenChange, contact, onVerified }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("idle"); // idle | running | result
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);

  const reset = () => {
    setPhase("idle");
    setResult(null);
    setApplying(false);
  };

  const handleOpenChange = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const run = async () => {
    setPhase("running");
    setResult(null);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/verify-entity`);
      setResult(data);
      setPhase("result");
    } catch (e) {
      toast.error(apiError(e), { duration: 12000, closeButton: true });
      setPhase("idle");
    }
  };

  const accept = async () => {
    const entity = result?.suggested_entity;
    if (!entity?.entity_name) {
      toast.error(t("verify.no_entity_to_accept", "No entity to accept"));
      return;
    }
    setApplying(true);
    try {
      await api.post(`/contacts/${contact.id}/reassign`, {
        create_company_name: entity.entity_name,
        new_position: entity.position || undefined,
        verification_note: `AI verified (${entity.confidence}%): ${entity.entity_name}` +
          (entity.country ? ` · ${entity.country}` : ""),
      });
      toast.success(t("verify.applied", "Contact reassigned to {{name}}",
        { name: entity.entity_name }));
      onVerified?.();
      handleOpenChange(false);
    } catch (e) {
      toast.error(apiError(e), { duration: 12000, closeButton: true });
    } finally {
      setApplying(false);
    }
  };

  const detach = async () => {
    setApplying(true);
    try {
      await api.post(`/contacts/${contact.id}/reassign`, {
        detach_only: true,
        verification_note: "AI verification flagged mismatch — detached from previous company",
      });
      toast.success(t("verify.detached", "Contact detached"));
      onVerified?.();
      handleOpenChange(false);
    } catch (e) {
      toast.error(apiError(e), { duration: 12000, closeButton: true });
    } finally {
      setApplying(false);
    }
  };

  if (!contact) return null;
  const entity = result?.suggested_entity;
  const confidence = entity?.confidence ?? 0;
  const confTone = confidence >= 80
    ? "text-emerald-600 dark:text-emerald-400 border-emerald-400/60"
    : confidence >= 50
      ? "text-amber-600 dark:text-amber-400 border-amber-400/60"
      : "text-destructive border-destructive/40";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl" data-testid="verify-entity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-primary" />
            {t("verify.title", "Verify contact entity via AI")}
          </DialogTitle>
        </DialogHeader>

        {phase === "idle" && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-amber-400/40 bg-amber-500/5 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="text-[12.5px] leading-relaxed">
                <div className="font-medium">{t("verify.warning_title", "Multinational corporate domain detected")}</div>
                <div className="text-muted-foreground mt-0.5">
                  {t("verify.warning_body",
                    "Contacts from generic group domains (veolia.com, siemens.com…) can belong to any subsidiary worldwide. Let the AI scrape LinkedIn and the web to determine where {{name}} actually works.",
                    { name: `${contact.first_name} ${contact.last_name}` })}
                </div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {t("verify.signals_used", "AI signals: LinkedIn page extract + Tavily web search + Claude 4.5 analysis. Takes ~25-40 seconds.")}
            </div>
            <Button onClick={run} className="w-full" data-testid="verify-start-btn">
              <Search className="h-3.5 w-3.5 mr-1.5" />
              {t("verify.start", "Run AI verification")}
            </Button>
          </div>
        )}

        {phase === "running" && (
          <div className="py-12 flex flex-col items-center justify-center text-muted-foreground" data-testid="verify-running">
            <Loader2 className="h-6 w-6 animate-spin mb-3" />
            <div className="text-sm">{t("verify.running", "Searching the web…")}</div>
            <div className="text-[11px] mt-1">{t("verify.running_hint", "LinkedIn extract → Tavily search → Claude analysis")}</div>
          </div>
        )}

        {phase === "result" && entity && (
          <div className="space-y-3" data-testid="verify-result">
            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-[10px] ${confTone}`} data-testid="verify-confidence">
                  {t("verify.confidence", "Confidence")}: {confidence}%
                </Badge>
                {entity.warning && (
                  <Badge variant="outline" className="text-[10px] border-amber-400/60 text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                    {t("verify.warning_label", "Warning")}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="flex items-start gap-2">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("verify.entity", "Entity")}</div>
                    <div className="font-medium" data-testid="verify-entity-name">{entity.entity_name || "—"}</div>
                    {entity.parent_group && entity.parent_group !== entity.entity_name && (
                      <div className="text-[11px] text-muted-foreground">{entity.parent_group}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("verify.location", "Location")}</div>
                    <div className="font-medium">
                      {entity.city ? `${entity.city}, ` : ""}{entity.country || "—"}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:col-span-2">
                  <Briefcase className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("verify.position", "Position")}</div>
                    <div className="font-medium truncate">{entity.position || "—"}</div>
                  </div>
                </div>
              </div>
              {entity.evidence && (
                <div className="text-[11.5px] text-muted-foreground border-l-2 border-primary/40 pl-2 leading-relaxed">
                  <span className="font-medium text-foreground">{t("verify.evidence", "Evidence")}:</span> {entity.evidence}
                </div>
              )}
              {entity.warning && (
                <div className="text-[11.5px] text-amber-600 dark:text-amber-400 leading-relaxed">
                  ⚠ {entity.warning}
                </div>
              )}
            </div>

            <div className="rounded-md border border-border/60 p-2.5 text-[11px] space-y-1">
              <div className="text-muted-foreground">
                {t("verify.currently_assigned", "Currently assigned to")}:{" "}
                <b className="text-foreground">{result.current_company_name || "—"}</b>
              </div>
              {result.evidence_sources?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {result.evidence_sources.slice(0, 4).map((u) => (
                    <a key={u} href={u} target="_blank" rel="noreferrer"
                      className="text-[10px] underline text-muted-foreground hover:text-primary truncate max-w-[200px] inline-flex items-center gap-0.5">
                      <ExternalLink className="h-2.5 w-2.5" /> {u.replace(/^https?:\/\//, "").slice(0, 30)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === "result" && (
            <>
              <Button variant="outline" onClick={detach} disabled={applying} data-testid="verify-detach-btn">
                {t("verify.detach", "Detach from current company")}
              </Button>
              <Button
                onClick={accept}
                disabled={applying || !entity?.entity_name || confidence < 50}
                data-testid="verify-accept-btn"
              >
                {applying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                {t("verify.accept", "Apply: reassign to {{name}}", { name: entity?.entity_name || "—" })}
              </Button>
            </>
          )}
          {phase !== "running" && (
            <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={applying}>
              {t("common.close", "Close")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
